import React, { useState, useEffect } from "react";
import { RouteComponentProps, Prompt } from "react-router";
import { Link } from "react-router-dom";
import {
  Grid,
  makeStyles,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  InputAdornment,
  Button
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import * as Yup from "yup";
import useInput from "../../../hooks/input/useInput";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import TrSelect from "../../shared/controls/TrSelect";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import ButtonConfirmDialog from "../../shared/common/ButtonConfirmDialog";
import TrSelectData from "../../../models/shared/TrSelectData";
import PaymentRequestDetails from "../../../models/admin/payment-request/PaymentRequestDetails";
import PaymentRequestStudentDetails from "../../../models/admin/payment-request/PaymentRequestStudentDetails";
import PaymentRequestSchoolCarrierDetails from "../../../models/admin/payment-request/PaymentRequestSchoolCarrierDetails";
import PaymentRequestAuthorizedCarrierDetails from "../../../models/admin/payment-request/PaymentRequestAuthorizedCarrierDetails";
import PaymentRequestStatus from "../../../models/payment-request-status/PaymentRequestStatus";
import PaymentRequestStatusEnum from "../../../models/payment-request-status/PaymentRequestStatusEnum";
import TrackerName from "../../../models/admin/payment-request/PaymentRequestTrackerName";
import PaymentRequestStatusApiClient from "../../../api/PaymentRequestStatusApiClient";
import PaymentRequestAdminApiClient from "../../../api/admin/PaymentRequestApiClient";
import {
  nameof,
  numberFormat,
  currencyFormat,
  getTooltipForDecimalInput,
  getCurrencyDisplayName,
  isSpecialCalculationFor2023Date,
} from "../../../utilities/utilities";
import useStudentDataInfo from "../../../hooks/student-data-info/useStudentDataInfo";
import PaymentRequestStatusElement from "../../shared/controls/PaymentRequestStatusElement";
import LockOpenIcon from '@material-ui/icons/LockOpen';
import { options } from "date-fns/locale/af";
import FoodType from "../../../models/shared/FoodType";
import { ProgramType } from "../../../models/financing-request/ProgramType";
import { SpecialNeedType } from "../../../models/special-need-type/SpecialNeedType";
import TeachingAidOptionType from "../../../models/shared/TeachingAidOptionType";
import AnnualAdministration from "../../../models/admin/annual-administration/AnnualAdministration";
import AnnualAdministrationApiClient from "../../../api/admin/AnnualAdministrationApiClient";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';

interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function PaymentRequestDetailsPage({ match }: Props) {
  const classes = useStyles({});
  const { id } = match.params;

  //anual prices 
  const [annualAdministrations, setAnnualAdministrations] = useState<
    AnnualAdministration[]
  >([]);

  useEffect(() => {
    async function getAnnualAdministrations() {
      const apiClient = new AnnualAdministrationApiClient();
      const administrations = await apiClient.getAnnualAdministrations();

      setAnnualAdministrations(administrations);
    }

    getAnnualAdministrations();
  }, []);
  //anual prices

  const [paymentRequestStatuses, setPaymentRequestStatuses] = useState<
    PaymentRequestStatus[]
  >([]);

  const [
    filteredPaymentRequestStatuses,
    setFilteredPaymentRequestStatuses,
  ] = useState<PaymentRequestStatus[]>([]);
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequestDetails>(
    {} as PaymentRequestDetails
  );

  const [showSave, setShowSave] = useState<boolean>(false);
  const [filterStatuses, setFilterStatuses] = useState<boolean>(false);

  const [initialStudentsDetails, setInitialStudentsDetails] = useState<
    PaymentRequestStudentDetails[]
  >([]);
  const [
    initialSchoolCarriersDetails,
    setInitialSchoolCarriersDetails,
  ] = useState<PaymentRequestSchoolCarrierDetails[]>([]);
  const [
    initialAuthorizedCarriersDetails,
    setInitialAuthorizedCarriersDetails,
  ] = useState<PaymentRequestAuthorizedCarrierDetails[]>([]);

  const [canStatusReturn, setCanStatusReturn] = useState<boolean>(false);

  const {
    handleTextChange,
    handleTextChangeArray,
    handleNumberChangeArray,
    handleSelectChange,
  } = useInput();
  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  const validationConfig = {
    trackerName: TrackerName.Details,
    validationScheme: Yup.object<PaymentRequestDetails>().shape({
      statusId: Yup.number(),
      changeReason: Yup.string().nullable().when("statusId", {
        is: PaymentRequestStatusEnum.Rejected,
        then: Yup.string().required(),
      }),
    }),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
  } = useChangeTracker([validationConfig], false);

  async function getPaymentRequestStatuses() {
    const apiClient = new PaymentRequestStatusApiClient();
    const statuses = await apiClient.getStatusesForSelect();

    setPaymentRequestStatuses(statuses);
    setFilteredPaymentRequestStatuses(statuses);
  }

  async function getPaymentRequestDetails() {
    const apiClient = new PaymentRequestAdminApiClient();
    const requestDetails = await apiClient.getDetails(id);

    setPaymentRequest(requestDetails);
    setInitialStudentsDetails(requestDetails.studentDetails);
    setInitialSchoolCarriersDetails(requestDetails.schoolCarrierDetails);
    setInitialAuthorizedCarriersDetails(
      requestDetails.authorizedCarrierDetails
    );

    setFilterStatuses(true);
  }

  useEffect(() => {
    return updateTrackerDebounceEffect([[TrackerName.Details, paymentRequest]]);
  }, [paymentRequest]);

  useEffect(() => {
    getPaymentRequestStatuses();
    getPaymentRequestDetails();
  }, []);

  useEffect(() => {
    if (filterStatuses) {
      handleSetFilteredStatuses();
    }
  }, [filterStatuses]);

  useEffect(() => {
    canReturnRequest();
  }, []);

  const {
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId,
    getAmountsTorecalculateCostAfterFebruary2023,
  } = useStudentDataInfo({ paymentRequestId: paymentRequest.id });

  async function canReturnRequest() {
    const apiClient = new PaymentRequestAdminApiClient();
    const canReturn = await apiClient.getCanStatusReturnRequest(+id)
    setCanStatusReturn(canReturn);
  }

  const handleStatusSave = async () => {
    if (!validationResult.isValid(false)) {
      showErrors();
      openSnackbar(
        "Ne možete spremiti podatke dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.",
        "error"
      );

      return;
    }

    const apiClient = new PaymentRequestAdminApiClient();

    await apiClient
      .updateStatus(
        (id as unknown) as number,
        paymentRequest.statusId,
        paymentRequest.changeReason
      )
      .then(function (response) {
        openSnackbar("Uspješno ste izmijenili status zahtjeva.", "success");
      });

    setShowSave(false);
    getPaymentRequestDetails();
  };

  const handleReturnStatusSave = async () => {
    const apiClient = new PaymentRequestAdminApiClient();

    await apiClient
      .updateStatus(
        (id as unknown) as number,
        PaymentRequestStatusEnum.Returned,
        ""
      )
      .then(function (response) {
        openSnackbar("Uspješno ste izmijenili status zahtjeva.", "success");
      });

    setShowSave(false);
    getPaymentRequestDetails();
  }

  const handleCostsSave = async () => {
    const apiClient = new PaymentRequestAdminApiClient();

    await apiClient
      .updateCorrectedCosts(
        (id as unknown) as number,
        paymentRequest.studentDetails,
        paymentRequest.schoolCarrierDetails,
        paymentRequest.authorizedCarrierDetails
      )
      .then(function (response) {
        openSnackbar("Uspješno ste izmijenili troškove.", "success");
      });

    getPaymentRequestDetails();

    setFilterStatuses(true);
  };

  const handleCostsClear = () => {
    setPaymentRequest((previousState: any) => ({
      ...previousState,
      studentDetails: initialStudentsDetails,
      schoolCarrierDetails: initialSchoolCarriersDetails,
      authorizedCarrierDetails: initialAuthorizedCarriersDetails,
    }));
  };

  const handleSetFilteredStatuses = () => {
    if (paymentRequest && paymentRequest.studentDetails) {
      // Set filtered status
      if (
        paymentRequest.studentDetails.filter(
          (s) =>
            s.transportCostCorrected ||
            s.teachingAidCostCorrected ||
            s.foodCostCorrected
        ).length > 0
      ) {
        var statusesWithoutReturned = paymentRequestStatuses.filter(
          (s) => s.id != PaymentRequestStatusEnum.Returned
        );

        setFilteredPaymentRequestStatuses([...statusesWithoutReturned]);
      } else {
        setFilteredPaymentRequestStatuses([...paymentRequestStatuses]);
      }
    }

    setFilterStatuses(false);
  };

  const calculateActualCost = () => {
    let resolvedCost: number = 0;
    if (paymentRequest && paymentRequest.studentDetails) {

      var currentAdministrationPrice = annualAdministrations.filter(aa => aa.schoolYearName === paymentRequest.schoolYearName);

      resolvedCost += paymentRequest.totalCost;
      paymentRequest.studentDetails.forEach((details) => {

        var recalculationHelp = getAmountsTorecalculateCostAfterFebruary2023(
          currentAdministrationPrice[0],
          details.foodTypes,
          details.numberOfDays || 0,
          details.foodCost || 0,
          details.teachingAidCost || 0,
          details.id,
          details.programType,
          details.specialNeedType
        );

        resolvedCost -= recalculationHelp === undefined ? 0 : recalculationHelp.food;
        resolvedCost += recalculationHelp === undefined ? 0 : recalculationHelp.teachinAid;

      });

    }
    return resolvedCost;
  }

  const calculateTotalResolvedCost = (updateCostAfterFebruary2023?: boolean) => {
    let resolvedCost: number = 0;

    if (paymentRequest && paymentRequest.studentDetails) {

      var currentAdministrationPrice = annualAdministrations.filter(aa => aa.schoolYearName === paymentRequest.schoolYearName);

      paymentRequest.studentDetails.forEach((details) => {

        //transport
        resolvedCost +=
          details.transportCostCorrected != null &&
            !isNaN(details.transportCostCorrected)
            ? details.transportCostCorrected
            : details.transportCost || 0;

        //food
        var foodCostToAdd = details.foodCostCorrected != null && !isNaN(details.foodCostCorrected)
          ? details.foodCostCorrected
          : details.foodCost || 0;

        resolvedCost += foodCostToAdd;

        var teachingAidCostToAdd =
          details.teachingAidCostCorrected != null &&
            !isNaN(details.teachingAidCostCorrected)
            ? details.teachingAidCostCorrected
            : details.teachingAidCost || 0;

        resolvedCost += teachingAidCostToAdd;

        if (updateCostAfterFebruary2023 === true) {
          var recalculationHelp = getAmountsTorecalculateCostAfterFebruary2023(
            currentAdministrationPrice[0],
            details.foodTypes,
            details.numberOfDays || 0,
            details.foodCost || 0,
            details.teachingAidCost || 0,
            details.id,
            details.programType,
            details.specialNeedType
          );


          resolvedCost -= recalculationHelp === undefined ? 0 : recalculationHelp.food;
          resolvedCost += recalculationHelp === undefined ? 0 : recalculationHelp.teachinAid;
        }
      });
    }

    if (paymentRequest && paymentRequest.schoolCarrierDetails) {
      paymentRequest.schoolCarrierDetails.forEach((details) => {
        resolvedCost +=
          details.transportCostCorrected != null &&
            !isNaN(details.transportCostCorrected)
            ? details.transportCostCorrected
            : details.transportCost || 0;
      });
    }

    if (paymentRequest && paymentRequest.authorizedCarrierDetails) {
      paymentRequest.authorizedCarrierDetails.forEach((details) => {
        resolvedCost +=
          details.transportCostCorrected != null &&
            !isNaN(details.transportCostCorrected)
            ? details.transportCostCorrected
            : details.transportCost || 0;
      });
    }

    return resolvedCost;
  };

  const handleStatusChange = (e: any) => {
    handleSelectChange(e, setPaymentRequest);
    setShowSave(true);
  };

  return paymentRequest.id ? (
    <div className={classes.root}>
      <Grid item xs={12}>
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/admin/payment-requests"
          style={{ marginBottom: "16px" }}
        >
          {<ArrowBackIcon />}
        </Button>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">
            Zahtjev za isplatom - {paymentRequest.schoolName} (
            {paymentRequest.schoolPlace})
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Detalji zahtjeva"
              titleTypographyProps={{ variant: "h6" }}
              classes={{ title: classes.title }}
              action={canStatusReturn ?
                <ButtonConfirmDialog
                  variant="outlined"
                  btnText={"OTKLJUČAJ"}
                  title={"Izmjena statusa"}
                  message={"Odabirom ove promjene otključat ćete zahtjev. Jeste li sigurni?"}
                  cancelBtnText={"Odustani"}
                  okBtnText={"Otključaj"}
                  onOkClick={() => handleReturnStatusSave()}
                  btnIcon={<LockOpenIcon className={classes.unlockIcon} />}
                /> : <></>
              }
            />
            <Divider />
            <CardContent>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Školska godina
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {paymentRequest.schoolYearName}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Broj učenika
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {paymentRequest.studentsNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Mjesec
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">{new Date(paymentRequest.validFrom).toLocaleString('hr-hr', { month: 'long', year: 'numeric' }).split(' ').join(' - ')}</Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Iznos
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {currencyFormat(paymentRequest.totalCost || 0, paymentRequest.currency)}
                  </Box>
                </Grid>
              </Grid>
              {
                //new Date(paymentRequest.validFrom).getFullYear() >= 2023 && new Date(paymentRequest.validFrom).getMonth() >= 1
                isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                  ?
                  <>
                    <Grid container item xs={12} spacing={2}>
                      <Grid item xs={12} md={2}>
                        <Box fontSize="body1.fontSize" fontWeight="bold">
                          Iznos od veljače 2023
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Box fontSize="body1.fontSize">
                          {currencyFormat(calculateActualCost(), paymentRequest.currency)}
                        </Box>
                      </Grid>

                    </Grid>

                  </>
                  : ''
              }
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Iznos s korekcijom
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    <Typography className={classes.resolvedCost}>
                      {currencyFormat(calculateTotalResolvedCost(
                        // new Date(paymentRequest.validFrom).getFullYear() == 2023 && new Date(paymentRequest.validFrom).getMonth() >= 1 && new Date(paymentRequest.validFrom).getMonth() <= 8
                        isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                      ), paymentRequest.currency)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              {
                //new Date(paymentRequest.validFrom).getFullYear() >= 2023 && new Date(paymentRequest.validFrom).getMonth() >= 1
                isSpecialCalculationFor2023Date(paymentRequest.validFrom)
                  ?
                  <>
                    <Grid item xs={12} md={10} >
                      <Box fontSize="body1.fontSize">
                        <br></br>
                        Nova cijena primijenjuje se za sve mjesečne zahtjeve kreirane od veljače 2023. godine sukladno izmjeni Odluke o kriterijima za financiranje povećanih troškova prijevoza i posebnih nastavnih sredstava i pomagala te sufinanciranja prehrane učenika s teškoćama u razvoju u osnovnoškolskim programima za školsku godinu 2022./2023.
                      </Box>
                    </Grid>
                  </>
                  : ''
              }
              <Grid
                container
                item
                xs={12}
                spacing={2}
                alignItems="center"
                justify="center"
              >
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Status zahtjeva
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {!paymentRequest.isReviewed ? (
                      <TrSelect
                        label={""}
                        value={paymentRequest.statusId}
                        name={nameof<typeof paymentRequest>("statusId")}
                        fullWidth={true}
                        selectData={
                          {
                            valueProp: "id",
                            textProp: "name",
                            data: filteredPaymentRequestStatuses,
                          } as TrSelectData<PaymentRequestStatus>
                        }
                        onChange={handleStatusChange}
                      />
                    ) : (
                      <PaymentRequestStatusElement
                        statusId={paymentRequest.statusId}
                        statusName={paymentRequest.statusName}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box fontSize="body1.fontSize">
                    {!paymentRequest.isReviewed &&
                      paymentRequest.statusId ===
                      PaymentRequestStatusEnum.Rejected ? (
                      <TrTextField
                        label="Razlog"
                        name={nameof<typeof paymentRequest>("changeReason")}
                        value={paymentRequest.changeReason || ""}
                        fullWidth
                        onChange={(e) => handleTextChange(e, setPaymentRequest)}
                        error={
                          !validationResult.isPropertyValid(
                            TrackerName.Details,
                            nameof<typeof paymentRequest>("changeReason")
                          )
                        }
                      />
                    ) : (
                      paymentRequest.changeReason
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box display="inline">
                    {showSave &&
                      paymentRequest.statusId !==
                      PaymentRequestStatusEnum.Submitted ? (
                      <ButtonConfirmDialog
                        btnText={"Spremi"}
                        title={"Izmjena statusa"}
                        message={"Želite li spremiti unesene izmjene?"}
                        cancelBtnText={"Odustani"}
                        okBtnText={"Spremi"}
                        onOkClick={() => handleStatusSave()}
                      />
                    ) : null}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item lg={12}>
          {paymentRequest.schoolCarrierDetails.map((details, index) => (
            <ExpansionPanel
              key={`${details.id.toString()}_${paymentRequest.isReviewed.toString()}`}
              expanded={paymentRequest.isReviewed ? false : undefined}
            >
              <ExpansionPanelSummary
                expandIcon={
                  paymentRequest.isReviewed ? null : <ExpandMoreIcon />
                }
              >
                <Grid container spacing={4}>
                  <Grid item md={2} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Vrsta vozila
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.vehicleTypeName}
                    </Typography>
                  </Grid>
                  <Grid item md={3} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Registracijske oznake
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.registrationPlate}
                    </Typography>
                  </Grid>
                  <Grid item md={3} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Ukupna dnevna kilometraža
                    </Typography>
                    <Typography variant="subtitle2">
                      {numberFormat(details.dailyMileageTotal, "km", 4)}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={1}>
                    <Typography variant="body2" color="textSecondary">
                      Broj učenika
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.numberOfStudentsTotal}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={1}>
                    <Typography variant="body2" color="textSecondary">
                      Broj dana
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.numberOfDays}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Cijena
                    </Typography>
                    <Typography variant="subtitle2">
                      {`${currencyFormat(
                        details.transportCost || 0, paymentRequest.currency
                      )} (${currencyFormat(
                        details.transportCostCorrected || 0, paymentRequest.currency
                      )})`}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={2} className={classes.largeColumn}>
                    <Typography variant="body2" color="textSecondary">
                      Napomena
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.remark}
                    </Typography>
                  </Grid>
                </Grid>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container spacing={4}>
                  <Grid item sm={3} md={3} className={classes.column}>
                    <TrTextField
                      label="Cijena"
                      name={nameof<typeof details>("transportCostCorrected")}
                      value={
                        details.transportCostCorrected != null &&
                          !isNaN(details.transportCostCorrected)
                          ? details.transportCostCorrected
                          : ""
                      }
                      type="number"
                      tooltip={getTooltipForDecimalInput()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{getCurrencyDisplayName(paymentRequest.currency)}</InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        handleNumberChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("schoolCarrierDetails")
                        )
                      }
                    />
                  </Grid>
                  <Grid item sm={3} md={3} className={classes.column}>
                    <TrTextField
                      label="Napomena"
                      name={nameof<typeof details>("remark")}
                      value={details.remark || ""}
                      fullWidth
                      onChange={(e) =>
                        handleTextChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("schoolCarrierDetails")
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}
        </Grid>
        <Grid item lg={12}>
          {paymentRequest.authorizedCarrierDetails.map((details, index) => (
            <ExpansionPanel
              key={`${details.id.toString()}_${paymentRequest.isReviewed.toString()}`}
              expanded={paymentRequest.isReviewed ? false : undefined}
            >
              <ExpansionPanelSummary
                expandIcon={
                  paymentRequest.isReviewed ? null : <ExpandMoreIcon />
                }
              >
                <Grid container spacing={4}>
                  <Grid item md={2} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Naziv prijevoznika
                    </Typography>
                    <Typography variant="subtitle2">{details.name}</Typography>
                  </Grid>
                  <Grid item md={3} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Ukupna dnevna kilometraža
                    </Typography>
                    <Typography variant="subtitle2">
                      {numberFormat(details.dailyMileage, "km", 4)}
                    </Typography>
                  </Grid>
                  <Grid item md={3} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Cijena po danu
                    </Typography>
                    <Typography variant="subtitle2">
                      {currencyFormat(details.dailyPrice, paymentRequest.currency)}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={1}>
                    <Typography variant="body2" color="textSecondary">
                      Broj učenika
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.numberOfStudentsTotal}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={1}>
                    <Typography variant="body2" color="textSecondary">
                      Broj dana
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.numberOfDays}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={2}>
                    <Typography variant="body2" color="textSecondary">
                      Cijena
                    </Typography>
                    <Typography variant="subtitle2">
                      {`${currencyFormat(
                        details.transportCost || 0, paymentRequest.currency
                      )} (${currencyFormat(
                        details.transportCostCorrected || 0, paymentRequest.currency
                      )})`}
                    </Typography>
                  </Grid>
                  <Grid item md={2} lg={2} className={classes.largeColumn}>
                    <Typography variant="body2" color="textSecondary">
                      Napomena
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.remark}
                    </Typography>
                  </Grid>
                </Grid>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container spacing={4}>
                  <Grid item sm={3} md={3} className={classes.column}>
                    <TrTextField
                      label="Cijena"
                      name={nameof<typeof details>("transportCostCorrected")}
                      value={
                        details.transportCostCorrected != null &&
                          !isNaN(details.transportCostCorrected)
                          ? details.transportCostCorrected
                          : ""
                      }
                      type="number"
                      tooltip={getTooltipForDecimalInput()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{getCurrencyDisplayName(paymentRequest.currency)}</InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        handleNumberChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>(
                            "authorizedCarrierDetails"
                          )
                        )
                      }
                    />
                  </Grid>
                  <Grid item sm={3} md={3} className={classes.column}>
                    <TrTextField
                      label="Napomena"
                      name={nameof<typeof details>("remark")}
                      value={details.remark || ""}
                      fullWidth
                      onChange={(e) =>
                        handleTextChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>(
                            "authorizedCarrierDetails"
                          )
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}
        </Grid>
        <Grid item lg={12}>
          {paymentRequest.studentDetails.map((details, index) => (
            <ExpansionPanel
              key={`${details.id.toString()}_${paymentRequest.isReviewed.toString()}`}
              expanded={paymentRequest.isReviewed ? false : undefined}
            >
              <ExpansionPanelSummary
                expandIcon={
                  paymentRequest.isReviewed ? null : <ExpandMoreIcon />
                }
              >
                <Grid container spacing={4} className={classes.container}>
                  <Grid item lg="auto" md={4} className={classes.column}>
                    <Typography variant="body2" color="textSecondary">
                      Učenik
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.lastName} {details.firstName}
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.birthDate}
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={4} className={classes.column}>
                    <Typography variant="body2" color="textSecondary">
                      OIB
                    </Typography>
                    <Typography variant="subtitle2">{details.oib}</Typography>
                  </Grid>
                  <Grid item lg="auto" sm={4} className={classes.largeColumn}>
                    <Typography variant="body2" color="textSecondary">
                      Razred
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.programName}
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.schoolClass + " - " + details.sectionMark}
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.largeColumn}>
                    <Typography variant="body2" color="textSecondary">
                      Vrsta odjeljenja
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.sectionTypeName}
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <Typography variant="body2" color="textSecondary">
                      Prijevoz
                    </Typography>
                    <Typography variant="subtitle2">
                      {getTransportOptionInfoByStudentId(details.id, paymentRequest.currency)}
                    </Typography>
                    <Typography variant="subtitle2">
                      Broj dana: {details.numberOfDays}
                      {", Cijena: " +
                        currencyFormat(details.transportCost || 0, paymentRequest.currency)}{" "}
                      ({currencyFormat(details.transportCostCorrected || 0, paymentRequest.currency)})
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <Typography variant="body2" color="textSecondary">
                      Prehrana
                    </Typography>
                    <Typography variant="subtitle2">
                      {getFoodOptionInfoByStudentId(details.id)}
                    </Typography>
                    <Typography variant="subtitle2">
                      Broj dana: {details.numberOfDays}
                      {", Cijena: " + currencyFormat(details.foodCost || 0, paymentRequest.currency)} (
                      {" " + currencyFormat(details.foodCostCorrected || 0, paymentRequest.currency)})
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <Typography variant="body2" color="textSecondary">
                      Posebna nastavna sredstva i pomagala
                    </Typography>
                    <Typography variant="subtitle2">
                      {getTeachingAidOptionInfoByStudentId(
                        details.id,
                        details.programType,
                        details.specialNeedType,
                        paymentRequest.currency,
                        annualAdministrations.filter(aa => aa.schoolYearName === paymentRequest.schoolYearName)[0]
                      )}
                    </Typography>
                    <Typography variant="subtitle2">
                      Broj dana: {details.numberOfDays}
                      {", Cijena: " +
                        currencyFormat(details.teachingAidCost || 0, paymentRequest.currency)}{" "}
                      ({currencyFormat(details.teachingAidCostCorrected || 0, paymentRequest.currency)})
                    </Typography>
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.largeColumn}>
                    <Typography variant="body2" color="textSecondary">
                      Napomena
                    </Typography>
                    <Typography variant="subtitle2">
                      {details.remark}
                    </Typography>
                  </Grid>
                </Grid>
              </ExpansionPanelSummary>
              <ExpansionPanelDetails>
                <Grid container spacing={4}>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <TrTextField
                      label="Prijevoz"
                      name={nameof<typeof details>("transportCostCorrected")}
                      value={
                        details.transportCostCorrected != null &&
                          !isNaN(details.transportCostCorrected)
                          ? details.transportCostCorrected
                          : ""
                      }
                      type="number"
                      tooltip={getTooltipForDecimalInput()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{getCurrencyDisplayName(paymentRequest.currency)}</InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        handleNumberChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("studentDetails")
                        )
                      }
                    />
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <TrTextField
                      label="Prehrana"
                      name={nameof<typeof details>("foodCostCorrected")}
                      value={
                        details.foodCostCorrected != null &&
                          !isNaN(details.foodCostCorrected)
                          ? details.foodCostCorrected
                          : ""
                      }
                      type="number"
                      tooltip={getTooltipForDecimalInput()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{getCurrencyDisplayName(paymentRequest.currency)}</InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        handleNumberChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("studentDetails")
                        )
                      }
                    />
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <TrTextField
                      label="Posebna nastavna sredstva i pomagala"
                      name={nameof<typeof details>("teachingAidCostCorrected")}
                      value={
                        details.teachingAidCostCorrected != null &&
                          !isNaN(details.teachingAidCostCorrected)
                          ? details.teachingAidCostCorrected
                          : ""
                      }
                      type="number"
                      tooltip={getTooltipForDecimalInput()}
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">{getCurrencyDisplayName(paymentRequest.currency)}</InputAdornment>
                        ),
                      }}
                      onChange={(e) =>
                        handleNumberChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("studentDetails")
                        )
                      }
                    />
                  </Grid>
                  <Grid item lg="auto" sm={3} className={classes.column}>
                    <TrTextField
                      label="Napomena"
                      name={nameof<typeof details>("remark")}
                      value={details.remark || ""}
                      fullWidth
                      onChange={(e) =>
                        handleTextChangeArray(
                          e,
                          index,
                          setPaymentRequest,
                          nameof<PaymentRequestDetails>("studentDetails")
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </ExpansionPanelDetails>
            </ExpansionPanel>
          ))}
        </Grid>
        <Grid item xs={12} className={classes.saveButtons}>
          {paymentRequest.isReviewed ? null : (
            <>
              <Box display="inline" mr="10px">
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => handleCostsClear()}
                >
                  Poništi
                </Button>
              </Box>
              <Box display="inline">
                <ButtonConfirmDialog
                  btnText={"Spremi"}
                  title={"Izmjena troškova"}
                  message={"Želite li spremiti unesene izmjene?"}
                  cancelBtnText={"Odustani"}
                  okBtnText={"Spremi"}
                  onOkClick={() => handleCostsSave()}
                />
              </Box>
            </>
          )}
        </Grid>
      </Grid>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />

      <Prompt
        when={showSave}
        message="Potvrdom ove akcije izgubit ćete podatke koje niste pohranili na trenutnom zahtjevu. Jeste li sigurni da želite odustati od ispunjavanja zahtjeva?"
      />
    </div>
  ) : null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  title: {
    fontWeight: 400,
  },
  container: {
    justifyContent: "space-between",
    padding: "10px 0",
  },
  column: {
    display: "flex",
    alignItems: "start",
    flexDirection: "column",
    flexGrow: 1,
    flexBasis: 0,
  },
  largeColumn: {
    display: "flex",
    alignItems: "start",
    flexDirection: "column",
    flexGrow: 1.5,
    flexBasis: 0,
  },
  resolvedCost: {
    color: theme.palette.error.main,
  },
  saveButtons: {
    textAlign: "right",
  },
  unlockIcon: {
    marginRight: theme.spacing(1),
  },
}));


